import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { signOut } from '@/services/auth.service';
import { UserRole } from '@/types/user-roles';
import { useNavigate } from 'react-router-dom';
import { AuthError, User as SupabaseUser } from '@supabase/supabase-js';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
}

interface AuthContextType {
  user: User | null;
  userRole: UserRole | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{
    data: { user: SupabaseUser | null };
    error: AuthError | null;
  }>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userRole: null,
  isAuthenticated: false,
  isLoading: true,
  logout: async () => {},
  signIn: async () => ({ data: { user: null }, error: null }),
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log("AuthProvider: Initializing auth state");
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setIsLoading(true);
        console.log("AuthProvider: Auth state changed", { event, hasSession: !!session });
        
        if (session?.user) {
          const role = session.user.user_metadata?.role as UserRole;
          console.log("AuthProvider: User authenticated", { 
            id: session.user.id,
            email: session.user.email,
            role: role || 'regular'
          });
          
          const userData = {
            id: session.user.id,
            email: session.user.email || '',
            firstName: session.user.user_metadata?.first_name,
            lastName: session.user.user_metadata?.last_name,
            role: role,
          };
          
          setUser(userData);
          setUserRole(userData.role || 'regular');
          console.log("AuthProvider: User role set to", userData.role || 'regular');
        } else {
          console.log("AuthProvider: No user session");
          setUser(null);
          setUserRole(null);
        }
        
        setIsLoading(false);
      }
    );

    // Initial session check
    const checkUser = async () => {
      try {
        console.log("AuthProvider: Checking initial user session");
        const { data } = await supabase.auth.getSession();
        
        if (data.session?.user) {
          const role = data.session.user.user_metadata?.role as UserRole;
          console.log("AuthProvider: Initial user found", { 
            id: data.session.user.id,
            email: data.session.user.email,
            role: role || 'regular',
            metadata: data.session.user.user_metadata
          });
          
          const userData = {
            id: data.session.user.id,
            email: data.session.user.email || '',
            firstName: data.session.user.user_metadata?.first_name,
            lastName: data.session.user.user_metadata?.last_name,
            role: role,
          };
          
          setUser(userData);
          setUserRole(userData.role || 'regular');
          console.log("AuthProvider: Initial user role set to", userData.role || 'regular');
        } else {
          console.log("AuthProvider: No initial user session");
        }
      } catch (error) {
        console.error("AuthProvider: Error checking user session:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkUser();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    try {
      console.log("AuthProvider: Logging out user");
      await signOut();
      setUser(null);
      setUserRole(null);
      window.location.href = '/';
    } catch (error) {
      console.error("AuthProvider: Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userRole,
        isAuthenticated: !!user,
        isLoading,
        logout,
        signIn: async (email: string, password: string) => {
          return await supabase.auth.signInWithPassword({
            email,
            password
          });
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
